import React, { useEffect, useCallback } from "react";
import styles from "../../../Styles/Modules/Button.module.scss";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { FaPlay } from "react-icons/fa6";
import { FaStop } from "react-icons/fa";
import { closeModal, openModal } from "../../../State/Slices/modalSlice";
import { playSound, stopSound } from "../../../State/Slices/soundsSlice";
import { SOUND_NAME } from "../../../sounds";

const ButtonPlay = ({ displayRef, handleSpin, handleStop }) => {
  const dispatch = useDispatch(); // Hook to dispatch actions to the Redux store
  const { t } = useTranslation(); // Hook to get translation function
  const { balance, bet } = useSelector((state) => state.game); // Select balance and bet from game state
  const { modalOpen } = useSelector((state) => state.modal); // Select modal open state
  const { spinComplete, isSpinning } = useSelector((state) => state.spin); // Select spin state
  const { autoPlayActive } = useSelector((state) => state.settings); // Select autoplay setting

  const canPlay = balance >= bet; // Determine if the player can play based on balance and bet
  const buttonTitle = canPlay ? t("spin") : t("stop"); // Set button title based on playability
  const IconComponent = !spinComplete || autoPlayActive ? FaStop : FaPlay; // Choose icon based on spin state

  // Define handleClick function using useCallback to memoize it
  const handleClick = useCallback(() => {
    const shouldCloseModal = modalOpen && modalOpen !== "credit"; // Determine if modal should be closed
    const spinNotComplete = spinComplete && canPlay && !isSpinning; // Check if spin is not complete and can play

    if (canPlay) {
      dispatch(playSound(SOUND_NAME.click)); // Play click sound
      dispatch(stopSound(SOUND_NAME.normalWin)); // Stop normal win sound

      if (displayRef.current) {
        displayRef.current.cancelAnimation(); // Cancel any ongoing animation
      }

      if (shouldCloseModal) {
        dispatch(closeModal()); // Close modal if necessary
      }
    } else {
      dispatch(playSound(SOUND_NAME.noCredits)); // Play no credits sound

      if (modalOpen !== "credit") {
        dispatch(openModal("credit")); // Open credit modal if not already open
      }
    }

    if (!spinComplete || autoPlayActive) {
      handleStop(); // Stop the spin if not complete or autoplay is active
    } else if (spinNotComplete) {
      handleSpin(); // Start the spin if conditions are met
    }
  }, [
    modalOpen,
    spinComplete,
    canPlay,
    isSpinning,
    autoPlayActive,
    dispatch,
    displayRef,
    handleStop,
    handleSpin,
  ]);

  // useEffect to handle keydown event for spacebar to trigger handleClick
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code === "Space" || event.key === " ") {
        event.preventDefault(); // Prevent default spacebar action
        handleClick(); // Trigger handleClick on spacebar press
      }
    };

    window.addEventListener("keydown", handleKeyDown); // Add event listener for keydown
    return () => {
      window.removeEventListener("keydown", handleKeyDown); // Clean up event listener on unmount
    };
  }, [handleClick]);

  // Render the button with appropriate styles, click handler, and icon
  return (
    <button
      className={`${styles.button} ${styles.buttonPlay}`} // Apply button styles
      onClick={handleClick} // Set click handler
      title={buttonTitle} // Set button title
    >
      <IconComponent className={styles.buttonIcon} />
    </button>
  );
};

export default ButtonPlay; // Export the ButtonPlay component as default
